import Grid from "@mui/material/Grid2";
import TextField from "@mui/material/TextField";
import React, { useEffect, useState } from "react";
import Autocomplete from '@mui/material/Autocomplete';
import useDebugInformation from "../../../../../../../../../hooks/useDebugInformation";
import { cacheUtils } from "../../../../../../../../..";
import { useAppSelector } from "@store/hooks";
import { CacheTypes } from "@utils/cache/interfaces";
import { ui } from "../../../../../../../../../interfaces/interfaces";
import useRecalculatePayments from "../../../../../../../../../hooks/useRecalculatePayments";
import { useCCPromoCodes } from "../../../../../../../../../hooks/useCareClubPromoCodes";
import { MemoizedInputForCarePlan } from "../../../../../../../../Shared/MemoizedInputForCarePlan";
import { CareClubCCMAPIModelsStoredProceduresSpServiceAgreementsGetValidCampaignCodesResult } from "@src/utilities/apis/ccm/model";

const name = "PromoTab"

const handleChangesOffer = (event: React.SyntheticEvent<Element, Event>, value: any) => {
    ui.CarePlanPlanDetailsOfferOfferType = value;
};

const handleChangesReason = (event: React.SyntheticEvent<Element, Event>, value: any) => {
    ui.CarePlanPlanDetailsOfferOfferReason = value;
};

type Props = {
    parent: string;
};

const offerReasons = () => cacheUtils.getCache<CacheTypes.OfferAllocationReasons>("OfferAllocationReasons");

const PromoTab: React.FC<Props> = ({ parent }) => {
    const prefix = "CarePlanPlanDetailsOffer";
    const availableOffers = useAppSelector(({ temp }) => temp.AvailableOffers);
    const boilerModel = useAppSelector(({ ui }) => ui.CarePlanPlanDetailsBoilerBoilerModel);
    const boilerInstallationDate = useAppSelector(({ ui }) => ui.CarePlanPlanDetailsBoilerBoilerInstallationDate);
    const boilerLastServicedDate = useAppSelector(({ ui }) => ui.CarePlanPlanDetailsBoilerBoilerLastServicedDate);
    const offerReason = useAppSelector(({ ui }) => ui.CarePlanPlanDetailsOfferOfferReason);
    const offerReasonHelperText = useAppSelector(({ ui }) => ui.CarePlanPlanDetailsOfferOfferReasonHelperText);
    const offerType = useAppSelector(({ ui }) => ui.CarePlanPlanDetailsOfferOfferType);
    const agreementDuration = useAppSelector(({ ui }) => ui.CarePlanPlanDetailsPlanOptionsDuration?.serviceAgreementDurationId);

    const offerTypeValid = useAppSelector(({ state }) => state.CarePlanPlanDetailsOfferOfferTypeValid);
    const offerReasonValid = useAppSelector(({ state }) => state.CarePlanPlanDetailsOfferOfferReasonValid);
    const promoCodeValid = useAppSelector(({ state }) => state.CarePlanPlanDetailsOfferPromoCodeValid);
    const promoCodes = useCCPromoCodes();
    const promoCode = useAppSelector(({ ui }) => ui.CarePlanPlanDetailsOfferPromoCode);

    const [disablePromoCode, setDisablePromoCode] = useState(false);
    const [disableOffers, setDisableOffers] = useState(false);
    const [matchedPromoCode, setMatchedPromoCode] = useState<CareClubCCMAPIModelsStoredProceduresSpServiceAgreementsGetValidCampaignCodesResult>({});
    const [adjustedOfferReasons] = useState([...offerReasons(), { serviceAgreementOfferAllocationReasonId: 2, description: "Promo Code" }]);

    useEffect(() => {
        setDisableOffers(!!(promoCode && promoCode.length > 0));
    }, [promoCode]);

    useEffect(() => {
        setMatchedPromoCode(promoCodes.find(code => code.campaignCode === promoCode) || {});
    }, [promoCode, promoCodes]);

    useEffect(() => {
        setDisablePromoCode(!!(offerType && offerType.serviceAgreementOfferId && !promoCodeValid));
    }, [offerType, promoCodeValid]);

    useEffect(() => {
        const {
            serviceAgreementDurationId,
            serviceAgreementOfferId,
            campaignCode
        } = matchedPromoCode;

        console.log("serviceAgreementDurationId", serviceAgreementDurationId);
        console.log("serviceAgreementOfferId", serviceAgreementOfferId);
        console.log("campaignCode", campaignCode);

        const isPromoCodeValid = promoCode && campaignCode && campaignCode === promoCode;
        const isDurationMatch = serviceAgreementDurationId === agreementDuration;

        if (isPromoCodeValid) {
            if (serviceAgreementDurationId === null || isDurationMatch) {
                ui.CarePlanPlanDetailsOfferOfferType = availableOffers.find(
                    offer => offer.serviceAgreementOfferId === serviceAgreementOfferId
                );
                ui.CarePlanPlanDetailsOfferOfferReason = adjustedOfferReasons.find(
                    reason => reason.serviceAgreementOfferAllocationReasonId === 2
                );
            } else if (!isDurationMatch) {
                const errorMessage = `Promo code is not valid for the selected duration. This promo code is valid for ${serviceAgreementDurationId} months agreements only.`;
                console.log(errorMessage);
                alert(errorMessage);
            }
        }
    }, [promoCode, promoCodes, availableOffers, adjustedOfferReasons, agreementDuration, matchedPromoCode]);

    useRecalculatePayments();

    const debugName = `${parent}▷[${name}]`;
    useDebugInformation(debugName, { prefix, parent, availableOffers, boilerInstallationDate, boilerLastServicedDate, boilerModel, offerReason, offerReasonHelperText, offerType, offerTypeValid, offerReasonValid });

    return (
        (<Grid container spacing={2}>
            <Grid
                size={{
                    xs: 12,
                    sm: 12
                }}>
                <MemoizedInputForCarePlan
                    inputName={`${prefix}PromoCode`}
                    parent={debugName}
                    prefix={prefix}
                    xs={12}
                    sm={12}
                    type="text"
                    tabName="PolicyTab"
                    disabled={disablePromoCode}
                    notRequired={offerReasonValid ? ["CarePlanPlanDetailsOfferPromoCode"] : []}
                />
            </Grid>
            <Grid
                size={{
                    xs: 12,
                    sm: 12
                }}>
                <Autocomplete
                    id="combo-box-offerTypes"
                    options={promoCodeValid ? availableOffers : availableOffers.filter(offer => offer.serviceAgreementOfferId !== 2)}
                    getOptionLabel={(options) => String(options.offerDescription)}
                    isOptionEqualToValue={(option, value) => option.serviceAgreementOfferId === value.serviceAgreementOfferId}
                    onChange={handleChangesOffer}
                    value={offerType?.serviceAgreementOfferId ? offerType : null}
                    disabled={disableOffers}
                    renderInput={(params) => <TextField required {...params} label="Offer Type" name={`${prefix}OfferType`} />}
                />
            </Grid>
            {offerTypeValid && (
                <Grid
                    size={{
                        xs: 12,
                        sm: 12
                    }}>
                    <Autocomplete
                        id="combo-box-offerReasons"
                        options={adjustedOfferReasons}
                        onChange={handleChangesReason}
                        getOptionLabel={(options) => String(options.description)}
                        isOptionEqualToValue={(option, value) => option.serviceAgreementOfferAllocationReasonId === value.serviceAgreementOfferAllocationReasonId}
                        value={offerReason?.serviceAgreementOfferAllocationReasonId ? offerReason : null}
                        renderInput={(params) => (
                            <TextField
                                required
                                {...params}
                                label="Offer Reason"
                                name={`${prefix}OfferReason`}
                                error={!offerReasonValid && offerTypeValid}
                                helperText={offerReasonHelperText}
                            />
                        )}
                        disabled={disableOffers}
                    />
                </Grid>
            )}
        </Grid>)
    );
};

export default PromoTab;
